declare global {
    interface Window {
        fbq?: (...args: unknown[]) => void;
        dataLayer: any[];
    }
}

type FbqParams = Record<string, unknown>;

const PROCESSED_FLAG = 'data-meta-processed';

function genEventId(name: string): string {
    const rnd =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `${Math.random().toString(36).slice(2)}${Date.now()}`;
    return `${name}_${rnd}`;
}

function relayToCapi(name: string, eventId: string, params?: FbqParams): void {
    try {
        void fetch('/_capi', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                event_name: name,
                event_id: eventId,
                event_source_url: window.location.href,
                custom_data: params ?? {},
            }),
            keepalive: true,
            credentials: 'same-origin',
        }).catch(() => undefined);
    } catch {
        // tracking must never break the page
    }
}

// The pixel base code sits in a data-cookiecategory block, so fbq only exists once
// the visitor accepts marketing cookies. That happens after DOMContentLoaded, when this
// component has already run, so events are queued until fbq shows up.
const pendingFbqCalls: Array<() => void> = [];
const FBQ_POLL_INTERVAL = 200;
const FBQ_MAX_POLLS = 150;
let fbqWatcher: number | undefined;
let fbqPolls = 0;

function flushPendingFbqCalls(): void {
    if (typeof window.fbq === 'function') {
        stopFbqWatcher();
        while (pendingFbqCalls.length > 0) {
            pendingFbqCalls.shift()?.();
        }
        return;
    }

    if (++fbqPolls >= FBQ_MAX_POLLS) {
        // Consent was not granted in a reasonable time, drop the queue.
        stopFbqWatcher();
        pendingFbqCalls.length = 0;
    }
}

function stopFbqWatcher(): void {
    if (fbqWatcher !== undefined) {
        window.clearInterval(fbqWatcher);
        fbqWatcher = undefined;
    }
    fbqPolls = 0;
}

function safeFbq(eventType: 'track' | 'trackCustom', name: string, params?: FbqParams, eventId?: string): void {
    const id = eventId ?? genEventId(name);
    const options = { eventID: id };

    const send = (): void => {
        if (typeof window.fbq !== 'function') {
            return;
        }
        window.fbq(eventType, name, params ?? {}, options);
        relayToCapi(name, id, params);
    };

    if (typeof window.fbq === 'function') {
        send();
        return;
    }

    pendingFbqCalls.push(send);
    if (fbqWatcher === undefined) {
        fbqWatcher = window.setInterval(flushPendingFbqCalls, FBQ_POLL_INTERVAL);
    }
}

function readAttr(selectorAttr: string, valueAttr: string): string | undefined {
    const el = document.querySelector(`[${selectorAttr}]`);
    const v = el?.getAttribute(valueAttr) ?? '';
    return v !== '' ? v : undefined;
}

function readJsonContainer(attr: string): FbqParams | null {
    const el = document.querySelector(`[${attr}]`);
    if (!el || el.getAttribute(PROCESSED_FLAG) === 'true') {
        return null;
    }
    el.setAttribute(PROCESSED_FLAG, 'true');

    const raw = el.getAttribute(attr);
    if (!raw) {
        return null;
    }
    try {
        return JSON.parse(raw) as FbqParams;
    } catch {
        return null;
    }
}

export default class MetaPixelTracking {
    public init(): void {
        this.dispatchPageEvents();

        document.addEventListener('click', this.handleEngagementClick, true);
        document.addEventListener('copy', this.handleCopy, true);
    }

    private dispatchPageEvents(): void {
        const viewContent = readJsonContainer('data-meta-view-content');
        if (viewContent) {
            safeFbq('track', 'ViewContent', viewContent);
        }

        const viewCategory = readJsonContainer('data-meta-view-category');
        if (viewCategory) {
            safeFbq('trackCustom', 'ViewCategory', viewCategory);
        }

        const initiateCheckout = readJsonContainer('data-meta-initiate-checkout');
        if (initiateCheckout) {
            safeFbq('track', 'InitiateCheckout', initiateCheckout);
        }

        const purchaseEventId = readAttr('data-meta-purchase', 'data-meta-purchase-event-id');
        const purchase = readJsonContainer('data-meta-purchase');
        if (purchase) {
            safeFbq('track', 'Purchase', purchase, purchaseEventId);
        }

        const addToCart = readJsonContainer('data-gtm-add-to-cart');
        if (addToCart) {
            safeFbq('track', 'AddToCart', this.mapGa4ToMeta(addToCart));
        }

        const addPaymentInfo = readJsonContainer('data-gtm-add-payment-info');
        if (addPaymentInfo) {
            safeFbq('track', 'AddPaymentInfo', this.mapGa4ToMeta(addPaymentInfo));
        }
    }

    private mapGa4ToMeta(ga4: FbqParams): FbqParams {
        const ecommerce = (ga4.ecommerce as FbqParams | undefined) ?? {};
        const items = (ecommerce.items as Array<Record<string, unknown>> | undefined) ?? [];
        const contentIds: string[] = [];
        const contents: Array<{ id: string; quantity: number; item_price: number }> = [];
        let numItems = 0;

        for (const item of items) {
            const id = String(item.item_id ?? '');
            const quantity = Number(item.quantity ?? 1);
            const price = Number(item.price ?? 0);
            contentIds.push(id);
            contents.push({ id, quantity, item_price: price });
            numItems += quantity;
        }

        return {
            content_ids: contentIds,
            contents,
            content_type: 'product',
            value: Number(ecommerce.value ?? 0),
            currency: String(ecommerce.currency ?? 'CZK'),
            num_items: numItems,
        };
    }

    private handleEngagementClick = (event: Event): void => {
        const target = event.target as HTMLElement | null;
        if (!target) {
            return;
        }

        const link = target.closest('a') as HTMLAnchorElement | null;
        if (link) {
            const href = link.getAttribute('href') ?? '';
            if (href.startsWith('mailto:')) {
                safeFbq('trackCustom', 'EmailClick', { href });
            } else if (href.startsWith('tel:')) {
                safeFbq('trackCustom', 'PhoneClick', { href });
            }
        }
        // ScheduleMeetingClick intentionally not tracked on MerchPP per spec.
    };

    private handleCopy = (): void => {
        const selection = window.getSelection?.()?.toString() ?? '';
        if (!selection) {
            return;
        }

        const emailMatch = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i.test(selection);
        if (emailMatch) {
            safeFbq('trackCustom', 'EmailCopy');
            return;
        }

        const phoneMatch = /(\+?\d[\d\s().-]{7,}\d)/.test(selection);
        if (phoneMatch) {
            safeFbq('trackCustom', 'PhoneCopy');
        }
    };
}
